import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scales } from '@phosphor-icons/react';

import { Button } from '../../components/ui/Button';
import { NumericInput } from '../../components/common/NumericInput';
import { DebtTermHint } from '../../components/liabilities/DebtTermHint';
import { useEvaluateDebtMutation } from '../../features/liabilities/queries';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { DEBT_TYPES, debtTypeLabel } from '../../components/liabilities/debtTypes';
import { effortVerdictBand } from '../../lib/debtDisplayUtils';
import { cn } from '../../utils/cn';
import { parsePtNumber } from '../../utils/money';

const PAGE_PAD = 'px-4 pb-8 pt-2 md:px-0';

function verdictLabel(result) {
  const band = result?.effort_band || effortVerdictBand(result?.effort_rate_after);
  const map = pt.liabilities.evaluator.verdict;
  if (band && map[band]) return map[band];
  return map[result?.verdict] || map.caution;
}

export function DividasEvaluator() {
  const evaluate = useEvaluateDebtMutation();
  const [form, setForm] = useState({
    type: 'personal_loan',
    monthly_payment: '',
    monthly_income: '',
  });

  const onEvaluate = () => {
    const payload = {
      type: form.type,
      monthly_payment: form.monthly_payment
        ? form.monthly_payment.replace(',', '.')
        : null,
    };
    if (form.monthly_income) {
      payload.monthly_income = form.monthly_income.replace(',', '.');
    }
    evaluate.mutate(payload);
  };

  const result = evaluate.data;
  const band =
    result?.effort_band ||
    (result?.effort_rate_after != null
      ? effortVerdictBand(result.effort_rate_after)
      : null);
  const after = Number(result?.effort_rate_after);
  const before = Number(result?.effort_rate_before);
  const gaugePct = Number.isNaN(after) ? 0 : Math.min(100, Math.max(0, after));
  // 40% mark on the track
  const markPct = 40;

  const tipLine = useMemo(() => {
    if (!result?.suggested_payment || !form.monthly_payment) return null;
    const suggested = Number(result.suggested_payment);
    const proposed = parsePtNumber(String(form.monthly_payment)) ?? 0;
    const income = Number(result.monthly_income_used || form.monthly_income);
    if (!(suggested > 0) || !(income > 0)) return null;
    const existing = Number(result.existing_monthly_payments || 0);
    const tipRate = Math.round(((existing + suggested) / income) * 1000) / 10;
    return pt.liabilities.evaluator.tipLowerPayment
      .replace('{suggested}', `€${suggested}`)
      .replace('{proposed}', `€${proposed}`)
      .replace('{rate}', String(tipRate));
  }, [result, form.monthly_payment, form.monthly_income]);

  return (
    <div className={cn('mx-auto flex max-w-lg flex-col gap-6', PAGE_PAD)}>
      <header>
        <Button asChild variant="ghost" className="mb-2 -ml-2 hidden md:inline-flex">
          <Link to={paths.dividas}>{pt.common.back}</Link>
        </Button>
        <h1 className="font-display text-2xl font-semibold text-foreground">
          {pt.liabilities.evaluator.title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {pt.liabilities.evaluator.subtitle}
        </p>
      </header>

      <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4">
        <label className="text-sm font-medium text-muted-foreground">
          {pt.liabilities.evaluator.typeLabel}
          <select
            className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          >
            {DEBT_TYPES.map((t) => (
              <option key={t} value={t}>
                {debtTypeLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-muted-foreground">
          {pt.liabilities.evaluator.paymentLabel}
          <div className="mt-1 flex items-center gap-2">
            <NumericInput
              className="flex-1"
              value={form.monthly_payment}
              onChange={(e) =>
                setForm((f) => ({ ...f, monthly_payment: e.target.value }))
              }
            />
            <span className="shrink-0 text-sm text-muted-foreground">
              {pt.liabilities.evaluator.paymentSuffix}
            </span>
          </div>
        </label>
        {result?.income_missing || form.monthly_income ? (
          <label className="text-sm font-medium text-muted-foreground">
            {pt.liabilities.evaluator.incomeLabel}
            <NumericInput
              className="mt-1"
              value={form.monthly_income}
              onChange={(e) =>
                setForm((f) => ({ ...f, monthly_income: e.target.value }))
              }
            />
          </label>
        ) : null}
        <Button
          onClick={onEvaluate}
          disabled={evaluate.isPending || !form.monthly_payment}
        >
          {evaluate.isPending ? pt.common.loading : pt.liabilities.evaluator.submit}
        </Button>
      </div>

      {result?.income_missing && !form.monthly_income ? (
        <p className="text-sm text-amber-800 dark:text-amber-300">
          {pt.liabilities.evaluator.missingIncome}
        </p>
      ) : null}

      {result && !result.income_missing && result.effort_rate_after != null ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-950/30">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-amber-200 bg-card text-amber-800 dark:border-amber-800 dark:text-amber-300">
              <Scales className="h-4 w-4" aria-hidden />
            </span>
            <p className="font-display text-base font-bold text-amber-900 dark:text-amber-200">
              {verdictLabel(result)}
            </p>
          </div>

          <div className="mt-4">
            <div className="relative h-3 overflow-hidden rounded-lg border border-amber-200 bg-card dark:border-amber-800">
              <div
                className="h-full rounded-lg bg-amber-500 dark:bg-amber-600"
                style={{ width: `${Math.min(100, (gaugePct / 60) * 100)}%` }}
              />
              <span
                className="absolute top-0 h-full w-0.5 bg-amber-800/70 dark:bg-amber-200/70"
                style={{ left: `${Math.min(100, (markPct / 60) * 100)}%` }}
                aria-hidden
              />
            </div>
            <div className="mt-2 flex items-center justify-center gap-2 font-display">
              <span className="text-sm tabular-nums text-muted-foreground">
                {before}%
              </span>
              <span className="text-muted-foreground" aria-hidden>
                →
              </span>
              <span className="text-xl font-bold tabular-nums text-amber-900 dark:text-amber-200">
                {after}%
              </span>
            </div>
          </div>

          {result.effort_rate_after != null ? (
            <p className="mt-3 text-xs leading-relaxed text-amber-900/90 dark:text-amber-100/90">
              A tua{' '}
              <span className="inline-flex items-center gap-1 font-semibold">
                taxa de esforço
                <DebtTermHint term="effortRate" variant="icon" />
              </span>{' '}
              (a fatia do rendimento que vai para dívidas){' '}
              {pt.liabilities.evaluator.effortFromTo
                .replace('{from}', String(before))
                .replace('{to}', String(after))}
              .{' '}
              {pt.liabilities.evaluator.bandHint[band] ||
                pt.liabilities.evaluator.bandHint.tight}
            </p>
          ) : null}
        </div>
      ) : null}

      {tipLine ? (
        <p className="rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          {tipLine}
        </p>
      ) : null}

      {result?.tips?.length > 0 && !tipLine ? (
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {result.tips.map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default DividasEvaluator;
